import React, { useState, useRef, useEffect, useMemo } from "react";
import { MultiStep, StepProps } from '@org/ui';
import { LearningPathForm, LearningPathFormRef } from '../forms/learning-path';
import { Table } from '@org/ui';
import { createColumnHelper } from '@tanstack/react-table';
import { trpc, RouterOutputs } from '@org/clients/school';
import { toast } from 'sonner';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@org/ui';
import { Button } from '@org/ui';
import { Separator } from '@org/ui';
import { Skeleton } from '@org/ui';
import { unwrapTrpcData, cn } from '@org/ui';
import { useDndSensors } from '../shared/use-dnd-sensors';
import { getMutationErrorMessage, hasOrderChanged } from '../shared/multi-step-utils';
import { SortableItemsCard } from '../shared/sortable-items-card';
import { resolveAfterSave, useStepNavigation } from '../shared/step-navigation';
import { UnsavedChangesDialog } from '../shared/unsaved-changes-dialog';
import { useTranslation } from 'react-i18next';

type PathDetail = RouterOutputs['learningPaths']['getById'];
type PathJourneyRow = NonNullable<PathDetail>['journeys'][number];
type JourneyRow = RouterOutputs['journeys']['getJourneys'][number];

// Row shape used by the ordered picker. `journeyId` + `order` come from the
// path; name/description are resolved from the journey catalogue so the
// picker never depends on which columns the getter joins in.
type SelectedJourney = {
  journeyId: string;
  name: string;
  description?: string | null;
  order: number;
};

type LearningPathMultiStepFormProps = {
  defaultValues: any;
  onSubmit: () => void;
  onClose: () => void;
  isEditing: boolean;
};

export const LearningPathMultiStepForm: React.FC<LearningPathMultiStepFormProps> = ({
  defaultValues,
  onSubmit,
  onClose,
  isEditing
}) => {
  const { t } = useTranslation('admin');
  const utils = trpc.useUtils();

  const steps: StepProps[] = [
    { title: t('learningPaths.form.steps.info') },
    { title: t('learningPaths.form.steps.journeys') },
    { title: t('learningPaths.form.steps.summary') }
  ];

  const states = ["info", "journeys", "summary"];
  // Creating = wizard (steps unlock as they are saved); editing = free navigation.
  const nav = useStepNavigation(steps.length, isEditing);

  const [pathData, setPathData] = useState<Record<string, any>>(defaultValues || {});
  const [selectedJourneys, setSelectedJourneys] = useState<SelectedJourney[]>([]);
  // What the server holds for the journeys step (loaded, then refreshed after each save).
  const [savedJourneys, setSavedJourneys] = useState<SelectedJourney[]>([]);
  // Unsaved edits on the info step (react-hook-form `isDirty`); `formKey` remounts it to discard them.
  const [stepDirty, setStepDirty] = useState(false);
  const [formKey, setFormKey] = useState(0);
  // Where the admin wanted to go when the unsaved-changes dialog opened.
  const [pendingTarget, setPendingTarget] = useState<number | null>(null);
  const [unsavedOpen, setUnsavedOpen] = useState(false);

  const sensors = useDndSensors();

  const formRef = useRef<LearningPathFormRef>(null);

  const { data: allJourneysData } = trpc.journeys.getJourneys.useQuery();
  const allJourneys = useMemo(
    () => unwrapTrpcData(allJourneysData) ?? [],
    [allJourneysData],
  );

  const pathId = defaultValues?.pathId;
  const { data: pathDetail, isLoading: isLoadingPathJourneys } =
    trpc.learningPaths.getById.useQuery({ pathId }, {
      enabled: isEditing && !!pathId
    });

  const setJourneysMutation = trpc.learningPaths.setJourneys.useMutation({
    onSuccess: async () => {
      toast.success(t('learningPaths.form.toasts.journeysSaved'));
      await utils.learningPaths.list.invalidate();
      if (pathId) {
        await utils.learningPaths.getById.invalidate({ pathId });
      }
    },
    onError: (error: unknown) => toast.error(t('learningPaths.form.toasts.journeysSaveFailed', { message: getMutationErrorMessage(error) })),
  });

  const toSelected = (row: PathJourneyRow, index: number): SelectedJourney => {
    const catalogue = allJourneys.find((j) => j.journeyId === row.journeyId);
    return {
      journeyId: row.journeyId,
      name: catalogue?.name ?? row.name ?? t('journeys.dialog.unnamedJourney'),
      description: catalogue?.description ?? null,
      order: row.order ?? index,
    };
  };

  useEffect(() => {
    const unwrapped = unwrapTrpcData<PathDetail>(pathDetail as PathDetail);
    const rows = unwrapped?.journeys ?? [];
    if (rows.length > 0) {
      const sorted = [...rows]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(toSelected);
      setSelectedJourneys(sorted);
      setSavedJourneys(sorted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathDetail, allJourneys]);

  /** Journeys step differs from what was last saved (selection or order). */
  const journeysUnsaved = (): boolean =>
    hasOrderChanged(selectedJourneys, savedJourneys, (j) => j.journeyId, (j, i) => j.order ?? i);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      setSelectedJourneys((items) => {
        const oldIndex = items.findIndex(item => item.journeyId === active.id);
        const newIndex = items.findIndex(item => item.journeyId === over.id);

        return arrayMove(items, oldIndex, newIndex).map((item, index) => ({
          ...item,
          order: index,
        }));
      });
    }
  };

  /** Save the journeys step when it has unsaved edits, then `onDone` (also when there was nothing to save). */
  const saveJourneys = (onDone?: () => void) => {
    if (!pathData.pathId || !journeysUnsaved()) {
      onDone?.();
      return;
    }
    const journeys = selectedJourneys;
    setJourneysMutation.mutate(
      { pathId: pathData.pathId, journeys: journeys.map((j, index) => ({ journeyId: j.journeyId, order: j.order ?? index })) },
      { onSuccess: () => { setSavedJourneys(journeys); onDone?.(); } },
    );
  };

  const stepHasUnsavedEdits = (index: number): boolean =>
    index === 0 ? stepDirty : index === 1 ? journeysUnsaved() : false;

  /** Move; in the wizard the next step unlocks only through `next()`. */
  const moveTo = (target: number) => {
    if (nav.mode === 'wizard' && target === nav.currentIndex + 1) nav.next();
    else nav.goTo(target);
  };

  /** Header / Back click: honour the wizard lock, and never drop unsaved edits silently. */
  const requestStep = (target: number) => {
    if (target === nav.currentIndex || !nav.canReach(target)) return;
    if (stepHasUnsavedEdits(nav.currentIndex)) {
      setPendingTarget(target);
      setUnsavedOpen(true);
      return;
    }
    moveTo(target);
  };

  const handleNext = () => {
    if (nav.currentIndex === 0) {
      setPendingTarget(null);
      formRef.current?.submit(); // → savePath moves on
      return;
    }
    if (nav.currentIndex === 1) {
      saveJourneys(() => nav.next());
      return;
    }
    nav.next();
  };

  const savePath = (data: Record<string, any>) => {
    setPathData({ ...pathData, ...data });
    setStepDirty(false);
    const target = resolveAfterSave(pendingTarget, nav.currentIndex, steps.length);
    setPendingTarget(null);
    setUnsavedOpen(false);
    moveTo(target);
  };

  const handleUnsavedSave = () => {
    if (nav.currentIndex === 0) {
      formRef.current?.submit(); // → savePath closes the dialog and moves
      return;
    }
    const target = pendingTarget;
    saveJourneys(() => {
      setUnsavedOpen(false);
      setPendingTarget(null);
      if (target !== null) moveTo(target);
    });
  };

  const handleUnsavedDiscard = () => {
    const target = pendingTarget;
    if (nav.currentIndex === 0) {
      setFormKey((k) => k + 1);
      setStepDirty(false);
    } else if (nav.currentIndex === 1) {
      setSelectedJourneys(savedJourneys);
    }
    setUnsavedOpen(false);
    setPendingTarget(null);
    if (target !== null) moveTo(target);
  };

  const handleUnsavedCancel = () => {
    setUnsavedOpen(false);
    setPendingTarget(null);
  };

  const toggleJourneySelection = (journey: JourneyRow) => {
    setSelectedJourneys(prevSelected => {
      const isSelected = prevSelected.some(item => item.journeyId === journey.journeyId);
      if (isSelected) {
        return prevSelected
          .filter(item => item.journeyId !== journey.journeyId)
          .map((item, index) => ({ ...item, order: index }));
      }
      return [...prevSelected, {
        journeyId: journey.journeyId,
        name: journey.name ?? t('journeys.dialog.unnamedJourney'),
        description: journey.description,
        order: prevSelected.length,
      }];
    });
  };

  const handleJourneyRemoval = (item: SelectedJourney) => {
    setSelectedJourneys(prev =>
      prev
        .filter(j => j.journeyId !== item.journeyId)
        .map((j, index) => ({ ...j, order: index })),
    );
  };

  const handleFinalSubmit = () => saveJourneys(() => onSubmit());

  const columnHelper = createColumnHelper<JourneyRow>();

  const journeyColumns = [
    columnHelper.accessor((row) => row.name || t('journeys.dialog.unnamedJourney'), {
      id: 'name',
      header: t('learningPaths.form.journeys.columnName'),
    }),
    columnHelper.accessor((row) => row.description || '', {
      id: 'description',
      header: t('learningPaths.form.journeys.columnDescription'),
    }),
    columnHelper.display({
      id: 'select',
      header: t('learningPaths.form.journeys.columnSelect'),
      cell: (info) => {
        const row = info.row.original;
        const isSelected = selectedJourneys.some(item => item.journeyId === row.journeyId);
        return (
          <Button
            variant="outline"
            size="sm"
            aria-pressed={isSelected}
            className={cn(isSelected && 'ring-2 ring-primary')}
            onClick={() => toggleJourneySelection(row)}
          >
            {isSelected ? t('learningPaths.form.journeys.selected') : t('learningPaths.form.journeys.select')}
          </Button>
        );
      },
    }),
  ];

  const yesNo = (value: unknown) =>
    value ? t('learningPaths.form.summary.yes') : t('learningPaths.form.summary.no');

  return (
    <div className="flex flex-col w-full min-h-screen bg-muted/30">
      <div className="w-full px-8 py-4 bg-background border-b shadow-sm">
        <MultiStep
          steps={steps}
          currentStep={nav.currentIndex}
          onChange={requestStep}
          mode={nav.mode}
          reachable={nav.reachable}
          labels={{ completed: t('common.steps.completed'), current: t('common.steps.current'), pending: t('common.steps.pending') }}
        />
      </div>

      <div className="w-full flex-grow px-6 pt-6 pb-28 sm:pb-6 max-w-5xl mx-auto mt-6">
        {states[nav.currentIndex] === "info" && (
          <Card>
            <CardHeader>
              <CardTitle>{t('learningPaths.form.info.title')}</CardTitle>
              <CardDescription>{t('learningPaths.form.info.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <LearningPathForm
                key={formKey}
                defaultValues={pathData}
                onSubmit={savePath}
                onClose={onClose}
                hideButtons={true}
                onDirtyChange={setStepDirty}
                ref={formRef}
              />
            </CardContent>
          </Card>
        )}
        {states[nav.currentIndex] === "journeys" && (
          <Card>
            <CardHeader>
              <CardTitle>{t('learningPaths.form.journeys.title')}</CardTitle>
              <CardDescription>{t('learningPaths.form.journeys.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingPathJourneys && isEditing ? (
                <div className="space-y-3">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                    {t('learningPaths.form.journeys.loading')}
                  </div>
                </div>
              ) : (
                <div className="mb-4">
                  <SortableItemsCard
                    items={selectedJourneys}
                    sensors={sensors}
                    onDragEnd={handleDragEnd}
                    onRemove={handleJourneyRemoval}
                    idKey="journeyId"
                    nameKey="name"
                    descriptionKey="description"
                    title={t('learningPaths.form.journeys.selectedTitle')}
                    emptyTitle={t('learningPaths.form.journeys.emptyTitle')}
                    emptyDescription={t('learningPaths.form.journeys.emptyDescription')}
                  />

                  <div className="mt-8">
                    <h3 className="font-medium mb-3 text-foreground border-b pb-2">{t('learningPaths.form.journeys.availableTitle')}</h3>
                    <Table
                      tableData={allJourneys}
                      columns={journeyColumns}
                      getRowId={(row: any) => row.journeyId}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        {states[nav.currentIndex] === "summary" && (
          <Card>
            <CardHeader>
              <CardTitle>{t('learningPaths.form.summary.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Card className="bg-muted/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-medium">{t('learningPaths.form.summary.infoTitle')}</CardTitle>
                  <Separator />
                </CardHeader>
                <CardContent className="space-y-2">
                  <p><span className="font-medium text-muted-foreground">{t('learningPaths.form.summary.nameLabel')}</span> {pathData.name}</p>
                  <p><span className="font-medium text-muted-foreground">{t('learningPaths.form.summary.descriptionLabel')}</span> {pathData.description || t('learningPaths.form.summary.none')}</p>
                  <p><span className="font-medium text-muted-foreground">{t('learningPaths.form.summary.activeLabel')}</span> {yesNo(pathData.isActive ?? true)}</p>
                  <p><span className="font-medium text-muted-foreground">{t('learningPaths.form.summary.defaultLabel')}</span> {yesNo(pathData.isDefault)}</p>
                </CardContent>
              </Card>

              {selectedJourneys.length > 0 && (
                <Card className="bg-muted/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-medium">{t('learningPaths.form.summary.journeysTitle', { count: selectedJourneys.length })}</CardTitle>
                    <Separator />
                  </CardHeader>
                  <CardContent>
                    <ol className="list-decimal pl-6 space-y-2">
                      {selectedJourneys.map(item => (
                        <li key={item.journeyId} className="text-muted-foreground">
                          <span className="font-medium">{item.name}</span>
                          {item.description && (
                            <p className="text-sm text-muted-foreground/70 mt-0.5">{item.description}</p>
                          )}
                        </li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <div className="mobile-sticky-action-bar w-full py-4 px-6 flex justify-between bg-background border-t shadow-sm sm:mt-auto">
        {nav.currentIndex > 0 && (
          <Button
            variant="outline"
            onClick={() => requestStep(nav.currentIndex - 1)}
          >
            {t('learningPaths.form.actions.back')}
          </Button>
        )}
        <div className="ml-auto">
          {!nav.isLast ? (
            <Button
              variant="outline"
              onClick={handleNext}
            >
              {t('learningPaths.form.actions.next')}
            </Button>
          ) : (
            <Button
              onClick={handleFinalSubmit}
              variant="outline"
              isLoading={setJourneysMutation.isPending}
              loadingText={t('common.status.saving')}
            >
              {t('learningPaths.form.actions.saveFinish')}
            </Button>
          )}
        </div>
      </div>

      <UnsavedChangesDialog
        open={unsavedOpen}
        onSave={handleUnsavedSave}
        onDiscard={handleUnsavedDiscard}
        onCancel={handleUnsavedCancel}
        saving={setJourneysMutation.isPending}
      />
    </div>
  );
};
